import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { X } from "lucide-react";

// Recurring GOLDEN easter-egg arrow (timed offers).
// Test mode: add ?goldegg=test to the URL → 3 appearances, 1 min apart.

const GOLD_OFFERS = [
    {
        title: { en: "−30% ON THE SIGNATURE PACK", fr: "−30% SUR LE PACK SIGNATURE" },
        body: {
            en: "Golden arrow caught. The 1'200 CHF Signature pack drops to 840 CHF — book a date this month.",
            fr: "Flèche d'or attrapée. Le pack Signature à 1'200 CHF passe à 840 CHF — réservez une date ce mois-ci.",
        },
        cta: { en: "BOOK WITH −30%", fr: "RÉSERVER AVEC −30%" },
        go: "offre",
        offer: { pack: "pack2", discount: 30, from: "1 200", to: "840" },
    },
    {
        title: { en: "VIP PASS — PRIVATE EVENT", fr: "PLACE VIP — EVENT PRIVÉ" },
        body: {
            en: "You unlocked a VIP spot for the next private Gloom event.",
            fr: "Vous débloquez une place VIP pour le prochain event privé Gloom.",
        },
        cta: { en: "CLAIM MY PASS", fr: "RÉCLAMER MA PLACE" },
        go: "vip",
    },
    {
        title: { en: "GLM RADIO — DISCOVER", fr: "GLM RADIO — DÉCOUVRIR" },
        body: {
            en: "Swiss artists are waiting for you on GLM Radio. Tune in.",
            fr: "Les artistes suisses vous attendent sur GLM Radio. Écoutez.",
        },
        cta: { en: "LISTEN NOW", fr: "ÉCOUTER" },
        go: "radio",
    },
];

// rare flash offer: −50% on pack 1 (Essentiel) or 3 (Premium) —
// only on promo days (every 5th day of the month), one 9-hour window at a time
const promoWindow = () => {
    const now = new Date();
    if (now.getDate() % 5 !== 0) return null;
    const block = Math.floor(now.getHours() / 9); // 0-9h / 9-18h / 18-24h
    const pack = block % 2 === 0
        ? { name: { en: "ESSENTIEL", fr: "ESSENTIEL" }, from: "750", to: "375" }
        : { name: { en: "PREMIUM", fr: "PREMIUM" }, from: "2'000", to: "1'000" };
    return {
        flash: true,
        title: { en: `FLASH −50% — PACK ${pack.name.en}`, fr: `FLASH −50% — PACK ${pack.name.fr}` },
        body: {
            en: `Rare golden arrow. The ${pack.from} CHF pack drops to ${pack.to} CHF — this window only.`,
            fr: `Flèche d'or rare. Le pack à ${pack.from} CHF passe à ${pack.to} CHF — uniquement pendant cette fenêtre.`,
        },
        cta: { en: "GRAB THE −50%", fr: "SAISIR LE −50%" },
        go: "offre",
        offer: block % 2 === 0
            ? { pack: "pack1", discount: 50, from: "750", to: "375" }
            : { pack: "pack3", discount: 50, from: "2 000", to: "1 000" },
    };
};

// production: 3min → 10min → 30min → 60min, then the cycle repeats
const PROD_SCHEDULE = [3, 10, 30, 60].map((m) => m * 60_000);
const TEST_SCHEDULE = [0, 60_000, 120_000]; // ?goldegg=test → 3 times in 3 minutes

export default function EggArrows({ page, go }) {
    const { lang } = useLang();
    const [gold, setGold] = useState(null); // offer object or null
    const [now, setNow] = useState(Date.now());
    const count = useRef(0);

    // ticking clock for the flash-offer countdown
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, []);

    // time left in the current 9-hour promo window (0-9h / 9-18h / 18-24h)
    const flashCountdown = () => {
        const d = new Date(now);
        const end = new Date(d);
        end.setHours(Math.floor(d.getHours() / 9) * 9 + 9, 0, 0, 0);
        const ms = Math.max(0, end - d);
        const h = Math.floor(ms / 3_600_000);
        const m = Math.floor((ms % 3_600_000) / 60_000);
        return lang === "fr" ? `EXPIRE DANS ${h}H ${String(m).padStart(2, "0")}MIN` : `EXPIRES IN ${h}H ${String(m).padStart(2, "0")}MIN`;
    };
    const testMode = useRef(
        typeof window !== "undefined" && window.location.search.includes("goldegg=test")
    );

    // golden egg schedule
    useEffect(() => {
        const schedule = testMode.current ? TEST_SCHEDULE : PROD_SCHEDULE;
        const timers = [];
        let cycleStart = 0;
        const plan = (base) => {
            schedule.forEach((delay, i) => {
                timers.push(
                    setTimeout(() => {
                        const flash = promoWindow();
                        setGold(flash || GOLD_OFFERS[count.current % GOLD_OFFERS.length]);
                        count.current += 1;
                    }, base + delay)
                );
            });
        };
        plan(0);
        if (!testMode.current) {
            // repeat the whole cycle every 106 minutes (3+10+30+60 + 3 margin)
            const cycle = setInterval(() => {
                cycleStart += 106 * 60_000;
                plan(0); // relative timeouts — recompute against remaining time
            }, 106 * 60_000);
            timers.push(cycle);
        }
        return () => timers.forEach((t) => (typeof t === "number" ? clearTimeout(t) : clearInterval(t)));
    }, []);

    const offer = gold;

    const claim = () => {
        if (!offer) return;
        if (offer.offer) window.__offer = offer.offer;
        go(offer.go);
        setGold(null);
    };

    return (
        <>
            {/* golden easter egg — center screen */}
            <AnimatePresence>
                {offer && (
                    <motion.div
                        key="gold-egg"
                        data-testid="gold-egg"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setGold(null)}
                        className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/70 backdrop-blur-sm"
                    >
                        {/* always-reachable close — fixed to the screen corner, big touch target */}
                        <button
                            data-testid="gold-egg-close"
                            onClick={(e) => {
                                e.stopPropagation();
                                setGold(null);
                            }}
                            className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 bg-ink/80 text-gold transition-colors hover:bg-gold hover:text-ink md:right-8 md:top-8"
                            aria-label="Fermer"
                        >
                            <X size={20} />
                        </button>
                        <motion.div
                            initial={{ scale: 0.4, y: 60, filter: "blur(10px)" }}
                            animate={{ scale: 1, y: 0, filter: "blur(0px)" }}
                            exit={{ scale: 0.7, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 60, damping: 14 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative flex max-h-[85vh] flex-col items-center overflow-y-auto px-6 py-10 text-center"
                        >
                            <motion.img
                                src="/img/arrow-3.png"
                                alt="Flèche d'or"
                                data-testid="gold-egg-arrow"
                                animate={{ rotateY: 360, y: [0, -12, 0] }}
                                transition={{
                                    rotateY: { duration: 6, repeat: Infinity, ease: "linear" },
                                    y: { duration: 2.6, repeat: Infinity, ease: "easeInOut" },
                                }}
                                className="h-44 w-auto select-none md:h-56"
                                style={{
                                    filter:
                                        "hue-rotate(115deg) saturate(1.6) brightness(1.25) drop-shadow(0 0 40px rgba(212,175,55,0.75))",
                                }}
                            />
                            <p
                                data-testid="gold-egg-title"
                                className="mt-8 font-display text-2xl font-extrabold uppercase tracking-tight text-gold md:text-4xl"
                            >
                                {offer.title[lang]}
                            </p>
                            <p className="mt-3 max-w-md font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                                {offer.body[lang]}
                            </p>
                            {offer.flash && (
                                <p
                                    data-testid="gold-egg-countdown"
                                    className="mt-4 animate-pulse border border-blood/60 bg-blood/15 px-4 py-2 font-mono text-[9px] tracking-[0.3em] text-blood"
                                >
                                    {flashCountdown()}
                                </p>
                            )}
                            <button
                                data-testid="gold-egg-cta"
                                onClick={claim}
                                className="mt-8 border border-gold bg-gold px-8 py-4 font-mono text-[10px] tracking-[0.3em] text-ink transition-all hover:tracking-[0.45em]"
                            >
                                {offer.cta[lang]}
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
